import React, { useRef, useEffect, useState } from 'react';
import { TextObject } from '../types';

interface Props {
  imageUrl: string;
  texts: TextObject[];
  selectedTextId: string | null;
  onTextsChange: (texts: TextObject[]) => void;
  onSelectText: (id: string | null) => void;
  isEditingText: boolean;
  setIsEditingText: (isEditing: boolean) => void;
  onTextUpdate: (id: string, newText: string) => void;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onImageLoaded?: (width: number, height: number) => void;
}

// Helper to convert hex to rgba
const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CanvasEditor: React.FC<Props> = ({ 
    imageUrl, 
    texts, 
    selectedTextId, 
    onTextsChange, 
    onSelectText,
    isEditingText,
    setIsEditingText,
    onTextUpdate,
    canvasRef,
    onImageLoaded
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  // State for dragging (Internal UI state)
  const [dragInfo, setDragInfo] = useState<{ startX: number; startY: number; initialX: number; initialY: number } | null>(null);
  
  // Snapping State
  const [snapGuides, setSnapGuides] = useState<{ x: boolean; y: boolean }>({ x: false, y: false });

  // Dimensions
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [scale, setScale] = useState(1);

  // Load Image and set Canvas Size
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = imageUrl;
    img.onload = () => {
        const w = img.naturalWidth;
        const h = img.naturalHeight;
        setCanvasSize({ width: w, height: h });
        if (onImageLoaded) {
            onImageLoaded(w, h);
        }
    };
  }, [imageUrl, onImageLoaded]);

  // Adjust Scale to Fit Container
  useEffect(() => {
      const updateScale = () => {
          if (containerRef.current && canvasSize.width > 0) {
              const containerWidth = containerRef.current.clientWidth;
              const containerHeight = containerRef.current.clientHeight;
              
              const scaleX = containerWidth / canvasSize.width;
              const scaleY = containerHeight / canvasSize.height;
              
              setScale(Math.min(scaleX, scaleY, 1)); // Max scale 1 (no upscaling visually)
          }
      };
      
      updateScale();
      window.addEventListener('resize', updateScale);
      return () => window.removeEventListener('resize', updateScale);
  }, [canvasSize]);

  // Handle Focus on Textarea for inline editing
  useEffect(() => {
      if (isEditingText && textareaRef.current && selectedTextId) {
          const textObj = texts.find(t => t.id === selectedTextId);
          if (textObj) {
              textareaRef.current.focus();
              textareaRef.current.value = textObj.text;
          }
      }
  }, [isEditingText, selectedTextId, texts]);

  const drawRoundedRect = (ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number) => {
      ctx.beginPath();
      ctx.moveTo(x + radius, y);
      ctx.lineTo(x + width - radius, y);
      ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
      ctx.lineTo(x + width, y + height - radius);
      ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
      ctx.lineTo(x + radius, y + height);
      ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
      ctx.lineTo(x, y + radius);
      ctx.quadraticCurveTo(x, y, x + radius, y);
      ctx.closePath();
  };

  const drawText = (ctx: CanvasRenderingContext2D, textObj: TextObject, isSelected: boolean, isEditing: boolean) => {
        if (isEditing) return; // Don't draw text on canvas if we are editing it (textarea is visible)

        ctx.save();
        
        // Translate to text center position
        ctx.translate(textObj.x, textObj.y);
        // Rotate
        ctx.rotate((textObj.rotation * Math.PI) / 180);

        // Font Settings
        ctx.font = `bold ${textObj.fontSize}px ${textObj.fontFamily}`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';

        const lines = textObj.text.split('\n');
        const lineHeight = textObj.fontSize * 1.2;
        const totalHeight = lines.length * lineHeight;
        const startY = -(totalHeight / 2) + (lineHeight / 2);

        // --- LAYER 0: BACKGROUND (New) ---
        if (textObj.isBackgroundEnabled) {
            let maxWidth = 0;
            lines.forEach(line => {
                const w = ctx.measureText(line).width;
                if (w > maxWidth) maxWidth = w;
            });

            // Visual correction: Move background up slightly to reduce bottom padding perceived visually
            const verticalCorrection = textObj.fontSize * 0.12; 

            const pad = textObj.backgroundPadding || 0;
            const bgWidth = maxWidth + (pad * 2);
            const bgHeight = totalHeight + (pad * 2);
            const bgX = -bgWidth / 2;
            const bgY = -(totalHeight / 2) - pad - verticalCorrection;

            ctx.save();
            ctx.fillStyle = hexToRgba(textObj.backgroundColor, (textObj.backgroundOpacity ?? 100) / 100);
            drawRoundedRect(ctx, bgX, bgY, bgWidth, bgHeight, textObj.backgroundRadius || 0);
            ctx.fill();
            ctx.restore();
        }

        // --- LAYER 1: NEON GLOW (Behind text) ---
        if (textObj.neonIntensity > 0) {
             ctx.save();
             ctx.shadowColor = textObj.neonColor;
             ctx.shadowBlur = textObj.neonIntensity;
             ctx.shadowOffsetX = 0;
             ctx.shadowOffsetY = 0;
             // Draw multiple times for stronger intensity
             const passes = 3;
             for (let i = 0; i < passes; i++) {
                 lines.forEach((line, index) => {
                     const yOffset = startY + (index * lineHeight);
                     ctx.fillStyle = textObj.neonColor; // Glow matches neon color
                     ctx.fillText(line, 0, yOffset);
                 });
             }
             ctx.restore();
        }

        // --- LAYER 2 & 3: STROKE & FILL (Outer Stroke Logic) ---
        lines.forEach((line, index) => {
            const yOffset = startY + (index * lineHeight);

            // 1. Draw Stroke (Background) - Outer Stroke Effect
            if (textObj.strokeWidth > 0) {
                ctx.save();
                
                // Shadow for stroke (since it's the outermost layer)
                if (textObj.isShadowEnabled && textObj.shadowOpacity > 0) {
                    const angleRad = (textObj.shadowAngle * Math.PI) / 180;
                    ctx.shadowColor = hexToRgba(textObj.shadowColor, textObj.shadowOpacity);
                    ctx.shadowBlur = textObj.shadowBlur;
                    ctx.shadowOffsetX = textObj.shadowDistance * Math.cos(angleRad);
                    ctx.shadowOffsetY = textObj.shadowDistance * Math.sin(angleRad);
                }

                ctx.strokeStyle = textObj.strokeColor;
                // Double width because fill will cover the inner half, leaving only the outer half visible
                ctx.lineWidth = textObj.strokeWidth * 2; 
                ctx.lineJoin = 'round';
                ctx.miterLimit = 2;
                ctx.strokeText(line, 0, yOffset);
                
                ctx.restore();
            }

            // 2. Draw Fill (Foreground)
            ctx.save();
            // Shadow for fill (only applied if NO stroke, otherwise the stroke already cast the shadow for the whole shape)
            if (textObj.strokeWidth === 0 && textObj.isShadowEnabled && textObj.shadowOpacity > 0) {
                 const angleRad = (textObj.shadowAngle * Math.PI) / 180;
                 ctx.shadowColor = hexToRgba(textObj.shadowColor, textObj.shadowOpacity);
                 ctx.shadowBlur = textObj.shadowBlur;
                 ctx.shadowOffsetX = textObj.shadowDistance * Math.cos(angleRad);
                 ctx.shadowOffsetY = textObj.shadowDistance * Math.sin(angleRad);
            }
            
            ctx.fillStyle = textObj.color;
            ctx.fillText(line, 0, yOffset);
            ctx.restore();
        });
        
        // --- SELECTION UI (Visual only, no transform applied) ---
        if (isSelected) {
            let maxWidth = 0;
            lines.forEach(line => {
                const w = ctx.measureText(line).width;
                if (w > maxWidth) maxWidth = w;
            });
            
            const verticalCorrection = textObj.isBackgroundEnabled ? (textObj.fontSize * 0.12) : 0;
            const padding = 20 + (textObj.isBackgroundEnabled ? (textObj.backgroundPadding || 0) : 0);
            
            ctx.strokeStyle = '#00F0FF';
            ctx.lineWidth = 2 / (scale > 0 ? 1 : 1); 
            ctx.setLineDash([10, 5]);
            
            // Draw rect around centered text (accounting for background size if exists)
            ctx.strokeRect(
                -(maxWidth / 2) - padding / 2,
                -(totalHeight / 2) - padding / 2 - verticalCorrection,
                maxWidth + padding,
                totalHeight + padding
            );
            
            // Rotation Handle Indicator (Top)
            ctx.beginPath();
            ctx.moveTo(0, -(totalHeight / 2) - padding / 2 - verticalCorrection);
            ctx.lineTo(0, -(totalHeight / 2) - padding / 2 - 20 - verticalCorrection);
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(0, -(totalHeight / 2) - padding / 2 - 20 - verticalCorrection, 6, 0, 2 * Math.PI);
            ctx.fillStyle = '#00F0FF';
            ctx.fill();
        }
        
        ctx.restore(); // Restore Rotation/Translation
  };

  // Render Canvas
  useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = imageUrl;
      
      img.onload = () => {
          // Clear
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          
          // Draw Background
          ctx.drawImage(img, 0, 0);
          
          // Draw Texts
          texts.forEach(textObj => {
             drawText(ctx, textObj, textObj.id === selectedTextId, textObj.id === selectedTextId && isEditingText);
          });

          // --- DRAW SNAP GUIDES ---
          // Draw guides ON TOP of everything if they are active
          if (snapGuides.x || snapGuides.y) {
              ctx.save();
              ctx.strokeStyle = '#00F0FF'; // Cyan snap color
              ctx.lineWidth = 2;
              ctx.setLineDash([5, 5]);

              const cx = canvas.width / 2;
              const cy = canvas.height / 2;

              if (snapGuides.x) {
                  ctx.beginPath();
                  ctx.moveTo(cx, 0);
                  ctx.lineTo(cx, canvas.height);
                  ctx.stroke();
              }

              if (snapGuides.y) {
                  ctx.beginPath();
                  ctx.moveTo(0, cy);
                  ctx.lineTo(canvas.width, cy);
                  ctx.stroke();
              }
              ctx.restore();
          }
      };
  }, [imageUrl, texts, selectedTextId, canvasSize, isEditingText, snapGuides]);

  // --- HIT DETECTION & INTERACTIONS ---

  const getCanvasCoordinates = (e: React.MouseEvent | React.TouchEvent) => {
      const canvas = canvasRef.current;
      if (!canvas) return { x: 0, y: 0 };
      
      const rect = canvas.getBoundingClientRect();
      const clientX = 'touches' in e ? e.touches[0].clientX : (e as React.MouseEvent).clientX;
      const clientY = 'touches' in e ? e.touches[0].clientY : (e as React.MouseEvent).clientY;
      
      return {
          x: (clientX - rect.left) * (canvas.width / rect.width),
          y: (clientY - rect.top) * (canvas.height / rect.height)
      };
  };

  const isPointInRotatedRect = (x: number, y: number, textObj: TextObject, ctx: CanvasRenderingContext2D) => {
      // 1. Measure Dimensions
      ctx.font = `bold ${textObj.fontSize}px ${textObj.fontFamily}`;
      const lines = textObj.text.split('\n');
      let maxWidth = 0;
      lines.forEach(line => {
          const w = ctx.measureText(line).width;
          if (w > maxWidth) maxWidth = w;
      });
      const lineHeight = textObj.fontSize * 1.2;
      const totalHeight = lines.length * lineHeight;
      
      const padding = 20 + (textObj.isBackgroundEnabled ? (textObj.backgroundPadding || 0) : 0);
      const width = maxWidth + padding;
      const height = totalHeight + padding;
      
      // Hit detection also needs to account for visual shift if background is enabled
      const verticalCorrection = textObj.isBackgroundEnabled ? (textObj.fontSize * 0.12) : 0;

      // 2. Translate point back relative to center
      const dx = x - textObj.x;
      const dy = y - textObj.y; 
      
      // 3. Rotate point backwards by -theta
      const angleRad = (-textObj.rotation * Math.PI) / 180;
      const rotatedX = dx * Math.cos(angleRad) - dy * Math.sin(angleRad);
      const rotatedY = dx * Math.sin(angleRad) + dy * Math.cos(angleRad);

      // 4. Check Bounds
      const hitWidth = maxWidth + (textObj.isBackgroundEnabled ? (textObj.backgroundPadding || 0) * 2 : 20);
      const hitHeight = totalHeight + (textObj.isBackgroundEnabled ? (textObj.backgroundPadding || 0) * 2 : 20);
      
      const yCenterOffset = -verticalCorrection;

      return (
          rotatedX >= -hitWidth / 2 &&
          rotatedX <= hitWidth / 2 &&
          rotatedY >= (-hitHeight / 2) + yCenterOffset &&
          rotatedY <= (hitHeight / 2) + yCenterOffset
      );
  }

  const handleMouseDown = (e: React.MouseEvent | React.TouchEvent) => {
      if (isEditingText) return; 

      const { x, y } = getCanvasCoordinates(e);
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      // Check items from top to bottom (reverse order)
      for (let i = texts.length - 1; i >= 0; i--) {
          const textObj = texts[i];
          if (isPointInRotatedRect(x, y, textObj, ctx)) {
              onSelectText(textObj.id);
              setDragInfo({ startX: x, startY: y, initialX: textObj.x, initialY: textObj.y });
              return;
          }
      }
      // Clicked on empty space
      onSelectText(null);
  };

  const handleMouseMove = (e: React.MouseEvent | React.TouchEvent) => {
      if (!dragInfo || !selectedTextId) return;
      
      e.preventDefault();
      const { x, y } = getCanvasCoordinates(e);
      const dx = x - dragInfo.startX;
      const dy = y - dragInfo.startY;
      
      let nextX = dragInfo.initialX + dx;
      let nextY = dragInfo.initialY + dy;

      // --- SNAP LOGIC ---
      const SNAP_THRESHOLD = 15; // pixels
      const centerX = canvasSize.width / 2;
      const centerY = canvasSize.height / 2;
      
      let newGuides = { x: false, y: false };

      // Snap X
      if (Math.abs(nextX - centerX) < SNAP_THRESHOLD) {
          nextX = centerX;
          newGuides.x = true;
      }
      
      // Snap Y
      if (Math.abs(nextY - centerY) < SNAP_THRESHOLD) {
          nextY = centerY;
          newGuides.y = true;
      }

      setSnapGuides(newGuides);

      // Update parent state directly during drag
      const updatedTexts = texts.map(t => 
          t.id === selectedTextId 
          ? { ...t, x: nextX, y: nextY } 
          : t
      );
      onTextsChange(updatedTexts);
  };

  const handleMouseUp = () => {
      setDragInfo(null);
      setSnapGuides({ x: false, y: false }); // Hide guides when dropped
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
      const { x, y } = getCanvasCoordinates(e);
      const canvas = canvasRef.current;
      const ctx = canvas?.getContext('2d');
      if (!ctx) return;

      for (let i = texts.length - 1; i >= 0; i--) {
          const textObj = texts[i];
          if (isPointInRotatedRect(x, y, textObj, ctx)) {
              onSelectText(textObj.id);
              setIsEditingText(true);
              return;
          }
      }
  };

  const finishEditing = () => {
      if (textareaRef.current && selectedTextId) {
          onTextUpdate(selectedTextId, textareaRef.current.value);
      }
      setIsEditingText(false);
  };

  const selectedText = texts.find(t => t.id === selectedTextId);

  // Helper to calculate textarea style props (clamped so textarea stays inside container and isn't clipped)
  const getTextAreaStyle = () => {
      if (!selectedText || !canvasSize.width || !containerRef.current) return { display: 'none' as const };

      const cx = selectedText.x;
      const cy = selectedText.y;
      const containerW = containerRef.current.clientWidth;
      const containerH = containerRef.current.clientHeight;
      const padding = 24;

      const visualX = cx * scale;
      const visualY = cy * scale;
      const rotation = selectedText.rotation;

      const canvasCenterOffsetX = canvasSize.width * scale / 2;
      const canvasCenterOffsetY = canvasSize.height * scale / 2;
      let centerX = containerW / 2 + (visualX - canvasCenterOffsetX);
      let centerY = containerH / 2 + (visualY - canvasCenterOffsetY);
      centerX = Math.max(padding, Math.min(containerW - padding, centerX));
      centerY = Math.max(padding, Math.min(containerH - padding, centerY));

      const marginLeft = centerX - containerW / 2;
      const marginTop = centerY - containerH / 2;

      return {
          position: 'absolute' as 'absolute',
          left: '50%',
          top: '50%',
          marginLeft,
          marginTop,
          transform: `translate(-50%, -50%) rotate(${rotation}deg)`,
          fontSize: `${selectedText.fontSize * scale}px`,
          fontFamily: selectedText.fontFamily,
          color: selectedText.color,
          WebkitTextStroke: selectedText.strokeWidth > 0 ? `${selectedText.strokeWidth * scale}px ${selectedText.strokeColor}` : '0',
          textAlign: 'center' as 'center',
          background: 'transparent',
          border: '1px dashed #00F0FF',
          outline: 'none',
          resize: 'none' as 'none',
          overflow: 'hidden',
          whiteSpace: 'pre',
          lineHeight: '1.2',
          minWidth: '100px',
          minHeight: '1em',
          maxWidth: `${containerW - padding * 2}px`,
          maxHeight: `${containerH - padding * 2}px`,
          zIndex: 50,
      };
  };

  return (
      <div 
        ref={containerRef} 
        className="w-full h-full min-h-0 relative flex items-center justify-center bg-click-dark overflow-hidden border-4 border-click-border rounded-lg"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onTouchStart={handleMouseDown}
        onTouchMove={handleMouseMove}
        onTouchEnd={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      >
          <canvas
              ref={canvasRef}
              width={canvasSize.width}
              height={canvasSize.height}
              style={{ 
                  width: canvasSize.width * scale, 
                  height: canvasSize.height * scale,
                  cursor: dragInfo ? 'grabbing' : 'grab'
              }}
          />
          
          {isEditingText && selectedText && (
              <textarea
                  ref={textareaRef}
                  style={getTextAreaStyle()}
                  className="bg-transparent"
                  onBlur={finishEditing}
                  onKeyDown={(e) => {
                      if (e.key === 'Escape') finishEditing();
                  }}
              />
          )}
      </div>
  );
};

export default CanvasEditor;